import { type z } from 'zod';

import { getPerformanceMark, type AppPerformanceProfilingEvent, type SerialisableValue } from '.';
import {
	AppFrameProfilingEventSchema,
	ClickedProfilingEventSchema,
	FCPProfilingEventSchema,
	LayoutShiftProfilingEventSchema,
	LCPProfilingEventSchema,
	NavigationTimingProfilingEventSchema,
	ResourceTimingProfilingEventSchema,
	UFOFrameEventSchema,
	type FrameProfilingEventType,
} from './frameEventValidations';

const ClickedProfilingCapturableEventTransform =
	ClickedProfilingEventSchema.transform<AppPerformanceProfilingEvent>((_) => {
		return {
			type: 'user-interacted',
			elapsed: getPerformanceMark(),
		};
	});

const NavigationTimingProfilingCapturableEventTransform =
	NavigationTimingProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'navigation-timing',
			elapsed: getPerformanceMark(),
			duration: Math.trunc(e.duration),
			transferSize: e.transferSize,
		};
	});

const ResourceTimingProfilingCapturableEventTransform =
	ResourceTimingProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'resource-timing',
			elapsed: getPerformanceMark(),
			start: Math.trunc(e.start),
			duration: Math.trunc(e.duration),
			transferSize: Math.trunc(e.transferSize),
			initiatorType: e.initiatorType,
		};
	});

const LayoutShiftProfilingCapturableEventTransform =
	LayoutShiftProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'dcls-score',
			elapsed: getPerformanceMark(),
			timestamp: Math.trunc(e.timestamp),
			// Transport 2dp truncated to avoid precision errors.
			score: Math.trunc(e.score * 100),
			inputFlag: e.inputFlag,
		};
	});

const FCPProfilingCapturableEventTransform =
	FCPProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'fcp-snapshot',
			elapsed: getPerformanceMark(),
			start: Math.trunc(e.start),
		};
	});

const LCPProfilingCapturableEventTransform =
	LCPProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'lcp-snapshot',
			elapsed: getPerformanceMark(),
			start: Math.trunc(e.start),
			size: Math.trunc(e.size),
		};
	});

const UFOFrameCapturableEventTransform =
	UFOFrameEventSchema.transform<AppPerformanceProfilingEvent>((e) => ({
		type: 'ufo-event',
		elapsed: getPerformanceMark(),
		name: e.name,
		payload: e.payload as Record<string, SerialisableValue>,
	}));

// Zod discriminated unions don't work with transforms on the narrowed types.
// Exhaustive-keyed map ensures future events added have transformers.
type ExhaustiveFrameEventTransformMap = {
	[K in FrameProfilingEventType['event']]: z.ZodEffects<
		z.ZodTypeAny,
		AppPerformanceProfilingEvent,
		FrameProfilingEventType
	>;
};

// For each in-frame event type, there should be a transformer to a safely ingestible event.
const transformers: ExhaustiveFrameEventTransformMap = {
	'navigation-timing': NavigationTimingProfilingCapturableEventTransform,
	'resource-timing': ResourceTimingProfilingCapturableEventTransform,
	'dcls-score': LayoutShiftProfilingCapturableEventTransform,
	click: ClickedProfilingCapturableEventTransform,
	'fcp-snapshot': FCPProfilingCapturableEventTransform,
	'lcp-snapshot': LCPProfilingCapturableEventTransform,
	'ufo-event': UFOFrameCapturableEventTransform,
};

export const FrameProfilingEventCaptureTransformer: z.ZodEffects<
	z.ZodDiscriminatedUnion<
		'event',
		[
			z.ZodObject<
				{
					event: z.ZodLiteral<'click'>;
				},
				'strip',
				z.ZodTypeAny,
				{
					event: 'click';
				},
				{
					event: 'click';
				}
			>,
			z.ZodObject<
				{
					duration: z.ZodNumber;
					event: z.ZodLiteral<'navigation-timing'>;
					transferSize: z.ZodNumber;
				},
				'strict',
				z.ZodTypeAny,
				{
					event: 'navigation-timing';
					duration: number;
					transferSize: number;
				},
				{
					event: 'navigation-timing';
					duration: number;
					transferSize: number;
				}
			>,
			z.ZodObject<
				{
					duration: z.ZodNumber;
					event: z.ZodLiteral<'resource-timing'>;
					initiatorType: z.ZodPipeline<
						z.ZodPipeline<
							z.ZodString,
							z.ZodCatch<
								z.ZodEnum<
									[
										'script',
										'fetch',
										'css',
										'link',
										'iframe',
										'img',
										'image',
										'xmlhttprequest',
										'other',
									]
								>
							>
						>,
						z.ZodString
					>;
					start: z.ZodNumber;
					transferSize: z.ZodNumber;
				},
				'strict',
				z.ZodTypeAny,
				{
					start: number;
					event: 'resource-timing';
					duration: number;
					transferSize: number;
					initiatorType: string;
				},
				{
					start: number;
					event: 'resource-timing';
					duration: number;
					transferSize: number;
					initiatorType: string;
				}
			>,
			z.ZodObject<
				{
					event: z.ZodLiteral<'dcls-score'>;
					inputFlag: z.ZodBoolean;
					score: z.ZodNumber;
					timestamp: z.ZodNumber;
				},
				'strict',
				z.ZodTypeAny,
				{
					event: 'dcls-score';
					inputFlag: boolean;
					score: number;
					timestamp: number;
				},
				{
					event: 'dcls-score';
					inputFlag: boolean;
					score: number;
					timestamp: number;
				}
			>,
			z.ZodObject<
				{
					event: z.ZodLiteral<'fcp-snapshot'>;
					start: z.ZodNumber;
				},
				'strict',
				z.ZodTypeAny,
				{
					start: number;
					event: 'fcp-snapshot';
				},
				{
					start: number;
					event: 'fcp-snapshot';
				}
			>,
			z.ZodObject<
				{
					event: z.ZodLiteral<'lcp-snapshot'>;
					size: z.ZodNumber;
					start: z.ZodNumber;
				},
				'strict',
				z.ZodTypeAny,
				{
					start: number;
					size: number;
					event: 'lcp-snapshot';
				},
				{
					start: number;
					size: number;
					event: 'lcp-snapshot';
				}
			>,
			z.ZodObject<
				{
					event: z.ZodLiteral<'ufo-event'>;
					name: z.ZodString;
					payload: z.ZodRecord<z.ZodString, z.ZodType<unknown, z.ZodTypeDef, unknown>>;
				},
				'strict',
				z.ZodTypeAny,
				{
					name: string;
					event: 'ufo-event';
					payload: Record<string, unknown>;
				},
				{
					name: string;
					event: 'ufo-event';
					payload: Record<string, unknown>;
				}
			>,
		]
	>,
	AppPerformanceProfilingEvent
> = AppFrameProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) =>
	transformers[e.event].parse(e),
);
